<!-- eslint-disable vue/multi-word-component-names -->
<script setup lang="ts">
  import {computed, useSlots, ref, onBeforeMount} from 'vue';
  import {getComponentConfig} from '@core/utils/getComponentConfig';
  import Icon from '@core/components/Icon.vue';

  interface Props {
    /**
     * Visual variant (classes / default icons). This is **not** the native HTML
     * `button` `type` attribute in general: only when this is `'submit'` does the
     * underlying `<button>` get `type="submit"`; otherwise it is `type="button"`.
     */
    type?:
      | 'standard'
      | 'cancel'
      | 'variation'
      | 'submit'
      | 'edit'
      | 'back'
      | 'delete-filled'
      | 'delete'
      | 'link'
      | 'pencil'
      | 'action';
    size?: 'x-small' | 'small' | 'default' | 'large';
    block?: boolean;
    loading?: boolean;
    disabled?: boolean;
    to?: string | object;
    href?: string;
    icon?: string | boolean;
    prependIcon?: string;
    appendIcon?: string;
    class?: string;
  }

  const props = withDefaults(defineProps<Props>(), {
    type: 'standard',
    size: 'default',
    block: false,
    loading: false,
    disabled: false,
    to: undefined,
    href: undefined,
    icon: undefined,
    prependIcon: undefined,
    appendIcon: undefined,
    class: '',
  });

  const BUTTON_PRIMARY_FILLED =
    'bg-brand-primary text-white hover:bg-brand-primary-500 rounded-md border border-brand-primary hover:border-brand-primary-500';

  const defaultConfig = {
    base: 'inline-flex items-center justify-center font-medium transition-colors duration-200 focus:outline-none cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed',
    sizes: {
      'x-small': 'px-2 py-1 text-xs',
      small: 'px-3 py-1.5 text-sm',
      default: 'px-4 py-2 text-base',
      large: 'px-6 py-3 text-lg',
    },
    iconSizes: {
      'x-small': 'p-1',
      small: 'p-1.5',
      default: 'p-2',
      large: 'p-3',
    },
    types: {
      standard: BUTTON_PRIMARY_FILLED,
      submit: BUTTON_PRIMARY_FILLED,
      edit: BUTTON_PRIMARY_FILLED,
      'delete-filled': 'bg-red-500 text-white hover:bg-red-600 rounded-md',
      cancel:
        'border border-gray-300 text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-800 rounded-md',
      variation:
        'border border-brand-primary text-brand-primary hover:bg-brand-primary/5 rounded-md',
      back: 'border border-brand-primary text-brand-primary hover:bg-brand-primary/5 rounded-md',
      delete:
        'text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-full',
      link: 'bg-brand-primary/10 text-brand-primary hover:bg-brand-primary/20 rounded-md',
      pencil:
        'bg-brand-primary/10 text-brand-primary hover:bg-brand-primary/20 rounded-md',
      action: 'text-brand-primary hover:bg-brand-primary/5 rounded-md',
    },
    icons: {
      pencil: 'pencil',
      delete: 'trash-2',
      link: 'link',
    },
  };

  const config = ref(defaultConfig);

  onBeforeMount(async () => {
    config.value = await getComponentConfig('Button', defaultConfig);
  });

  const componentTag = computed(() => {
    if (props.to) return 'router-link';
    if (props.href) return 'a';
    return 'button';
  });

  const slots = useSlots();
  const hasDefaultSlot = computed(() => {
    return !!slots.default;
  });

  /**
   * Maps button `size` to Icon `size`: `x-small` / `small` map 1:1; `default` and
   * `large` use `small` icons so glyphs stay proportional next to button text.
   */
  const getIconSize = () => {
    if (props.size === 'x-small') return 'x-small';
    if (props.size === 'small') return 'small';
    return 'small';
  };

  const buttonClasses = computed(() => {
    const baseClasses = [
      'button',
      config.value.base,
      props.block ? 'w-full' : '',
      props.class,
    ];

    // Size classes
    if (props.icon) {
      baseClasses.push(config.value.iconSizes[props.size]);
    } else {
      baseClasses.push(config.value.sizes[props.size]);
    }

    const typeClass =
      config.value.types[props.type] || config.value.types.standard;
    baseClasses.push(typeClass);

    // If it's an icon button
    if (props.icon) {
      baseClasses.push('aspect-square');
      if (typeof props.icon === 'boolean') {
        baseClasses.push('rounded-full');
      }
    }

    return baseClasses.join(' ');
  });

  const htmlType = computed(() => {
    if (props.type === 'submit') return 'submit';
    return 'button';
  });

  const internalIcon = computed(() => {
    const iconName =
      config.value.icons[props.type as keyof typeof config.value.icons];
    if (iconName) return iconName;
    if (typeof props.icon === 'string') return props.icon;
    return null;
  });

  const isDisabled = computed(() => props.disabled || props.loading);

  const isNativeButton = computed(() => componentTag.value === 'button');

  const onLinkClick = (event: MouseEvent) => {
    if (isDisabled.value && !isNativeButton.value) {
      event.preventDefault();
      event.stopPropagation();
    }
  };
</script>

<template>
  <component
    :is="componentTag"
    :type="componentTag === 'button' ? htmlType : undefined"
    :to="to"
    :href="href"
    :disabled="isNativeButton ? isDisabled : undefined"
    :aria-disabled="!isNativeButton && isDisabled ? true : undefined"
    :tabindex="!isNativeButton && isDisabled ? -1 : undefined"
    :class="[
      buttonClasses,
      !isNativeButton && isDisabled ? 'pointer-events-none' : '',
    ]"
    v-bind="$attrs"
    @click="onLinkClick">
    <!-- Loading: show spinner only for icon buttons, spinner + text for buttons with slot -->
    <Icon
      v-if="loading && !hasDefaultSlot"
      name="loader-2"
      class="animate-spin"
      :size="getIconSize()" />
    <template v-else-if="loading">
      <Icon
        name="loader-2"
        class="animate-spin me-2"
        :size="getIconSize()" />
      <slot />
    </template>
    <template v-else>
      <!-- Prepend Icon -->
      <Icon
        v-if="prependIcon"
        :name="prependIcon"
        :class="hasDefaultSlot ? 'me-2' : ''"
        :size="getIconSize()" />

      <!-- Main Icon (for icon buttons or specific types) -->
      <Icon
        v-if="internalIcon"
        :name="internalIcon"
        :class="hasDefaultSlot ? 'me-2' : ''"
        :size="getIconSize()" />

      <!-- Default Slot -->
      <slot />

      <!-- Append Icon -->
      <Icon
        v-if="appendIcon"
        :name="appendIcon"
        class="ms-2"
        :size="getIconSize()" />
    </template>
  </component>
</template>
